import React, { useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const YouTubeTranscriptPage: React.FC = () => {
  const [url, setUrl] = useState<string>('');
  const [transcript, setTranscript] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [copyStatus, setCopyStatus] = useState<string>('');

  const getVideoId = (youtubeUrl: string) => {
    try {
      const urlObj = new URL(youtubeUrl);
      return urlObj.searchParams.get('v');
    } catch (e) {
      return null;
    }
  };

  const checkTranscriptInFirestore = async (videoId: string) => {
    try {
      const docRef = doc(db, 'transcripts', videoId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.transcript || null;
      }
      return null;
    } catch (err) {
      console.error('Error checking Firestore:', err);
      throw new Error('Failed to check Firestore.');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setTranscript('');

    try {
      const videoId = getVideoId(url);
      if (!videoId) {
        throw new Error('Invalid YouTube URL. Could not parse video ID.');
      }

      // Initial Firestore Check
      let fetchedTranscript = await checkTranscriptInFirestore(videoId);

      if (fetchedTranscript) {
        setTranscript(fetchedTranscript);
        setLoading(false);
        return;
      }

      // If not found, send the URL to the backend
      await fetch(`${backendUrl}/process_url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      }).then(response => response.json())
        .then(data => {
          console.log('Response from backend:', data); // Log backend response
        })
        .catch(error => {
          console.error('Error communicating with backend:', error); // Log fetch errors
        });
    

      // Polling Firebase
      let attempts = 0;
      while (attempts < 2) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        fetchedTranscript = await checkTranscriptInFirestore(videoId);
        if (fetchedTranscript) {
          setTranscript(fetchedTranscript);
          break;
        }
        attempts++;
      }

      if (!fetchedTranscript) {
        setError('Transcript not found after multiple attempts.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch transcript. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (err) {
      setCopyStatus('Copy failed');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="ml-2 text-xl font-bold">YouTube Transcript Tool</span>
            </div>
          </div>
        </nav>
      </header>
      <main className="flex-1 bg-gradient-to-r from-blue-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Get YouTube Video Transcripts Instantly
            </h1>
          </div>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg text-white font-medium transition-all ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700'}`}
              >
                {loading ? 'Processing...' : 'Check Transcript Availability'}
              </button>
            </form>
            {error && <div className="mt-4 text-red-600">{error}</div>}
            {loading && <div className="mt-4">Loading... Please wait</div>}
            {transcript && (
              <div className="mt-4">
                <h2 className="text-lg font-bold">Transcript:</h2>
                <pre className="bg-gray-100 p-4 rounded-lg mt-2 whitespace-pre-wrap">{transcript}</pre>
                <button
                  onClick={copyToClipboard}
                  className="mt-4 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Copy to Clipboard
                </button>
                {copyStatus && <span className="ml-4 text-green-600">{copyStatus}</span>}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default YouTubeTranscriptPage;
